import { ApiService } from "./ApiService";
import type {
  AvailabilitySlot,
  CreateLeadPayload,
} from "@/domain/configurator";

export interface ScheduleMeetingPayload {
  slot: {
    date: string;
    availabilityWindow: string;
  };
  packageId?: string;
  packageName?: string;
}

export interface ScheduleMeetingResponse {
  success: boolean;
  meetingId: string;
}

export interface ScheduleMeetingIntentPayload {
  email: string;
  firstName: string;
  lastName?: string;
  company?: string;
  eventTypeUri: string;
  turnstileToken: string;
  leadId?: string;
}

export interface ScheduleMeetingIntentResponse {
  success: boolean;
  message?: string;
}

export interface ConfirmMeetingPayload {
  token: string;
  turnstileToken: string;
  date?: string;
  timeSlot?: string;
}

export interface ConfirmMeetingResponse {
  success: boolean;
  meetingId?: string;
  zoomLink?: string;
  date?: string;
  timeSlot?: string;
}

export interface SignupPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  turnstileToken: string;
}

export interface SendVerificationCodePayload {
  email: string;
}

export interface SendVerificationCodeResponse {
  success: boolean;
  alreadyVerified?: boolean;
}

export interface ConfirmVerificationCodePayload {
  email: string;
  code: string;
}

export interface ConfirmVerificationCodeResponse {
  success: boolean;
}

export interface EmailVerificationStatusResponse {
  verified: boolean;
}

export interface SubmitPackageRequestResponse {
  success: boolean;
  requestId: string;
  leadId: string;
  uploadUrls?: { fileName: string; uploadUrl: string; s3Key: string }[];
  message?: string;
}

export interface SendOnboardingEmailCodePayload {
  email: string;
  turnstileToken?: string;
}

export interface SendOnboardingEmailCodeResponse {
  success: boolean;
}

export interface VerifyOnboardingEmailCodePayload {
  email: string;
  code: string;
}

export interface VerifyOnboardingEmailCodeResponse {
  success: boolean;
}

export interface EventDetailsResponse {
  name: string;
  startTime: string;
  endTime: string;
  locationType: string | null;
  locationLabel: string | null;
}

export interface CreateNdaContractPayload {
  leadId: string;
  name: string;
  email: string;
  summary: string;
}

export interface CreateNdaContractResponse {
  envelopeId: string;
  signingUrl: string;
  s3Key: string;
}

export interface NdaStatusResponse {
  status: "pending" | "signed" | "declined" | "voided";
}

export class OnboardingService {
  constructor(private readonly apiService: ApiService) {}

  async getAvailabilitySlots(): Promise<{ slots: AvailabilitySlot[] }> {
    return this.apiService.request<{ slots: AvailabilitySlot[] }>(
      "/onboarding/availability-slots",
      {
        method: "GET",
        requireAuth: false,
      },
    );
  }

  async createLead(
    payload: CreateLeadPayload,
  ): Promise<{ success: boolean; leadId: string }> {
    return this.apiService.request<{ success: boolean; leadId: string }>(
      "/onboarding/create-lead",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      },
    );
  }

  async getUploadUrl(
    fileName: string,
    contentType: string,
  ): Promise<{ uploadUrl: string; key: string }> {
    return this.apiService.request<{ uploadUrl: string; key: string }>(
      "/onboarding/upload-url",
      {
        method: "POST",
        body: JSON.stringify({ fileName, contentType }),
        requireAuth: false,
      },
    );
  }

  async scheduleMeeting(
    payload: ScheduleMeetingPayload,
  ): Promise<ScheduleMeetingResponse> {
    return this.apiService.request<ScheduleMeetingResponse>(
      "/schedule-meeting",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: true,
      },
    );
  }

  async scheduleMeetingIntent(
    payload: ScheduleMeetingIntentPayload,
  ): Promise<ScheduleMeetingIntentResponse> {
    return this.apiService.request<ScheduleMeetingIntentResponse>(
      "/onboarding/scheduleMeeting",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      },
    );
  }

  async confirmMeeting(
    payload: ConfirmMeetingPayload,
  ): Promise<ConfirmMeetingResponse> {
    return this.apiService.request<ConfirmMeetingResponse>(
      "/onboarding/confirm-meeting",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      },
    );
  }

  async cancelMeeting(
    meetingId: string,
    token?: string,
    reason?: string,
  ): Promise<{ success: boolean; rescheduleUrl?: string }> {
    return this.apiService.request<{
      success: boolean;
      rescheduleUrl?: string;
    }>("/onboarding/cancel-meeting", {
      method: "POST",
      body: JSON.stringify({ meetingId, token, reason }),
      requireAuth: false,
    });
  }

  async sendEmailCode(
    payload: SendOnboardingEmailCodePayload,
  ): Promise<SendOnboardingEmailCodeResponse> {
    return this.apiService.request<SendOnboardingEmailCodeResponse>(
      "/onboarding/send-email-code",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      },
    );
  }

  async verifyEmailCode(
    payload: VerifyOnboardingEmailCodePayload,
  ): Promise<VerifyOnboardingEmailCodeResponse> {
    return this.apiService.request<VerifyOnboardingEmailCodeResponse>(
      "/onboarding/verify-email-code",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      },
    );
  }

  async createNdaContract(
    payload: CreateNdaContractPayload,
  ): Promise<CreateNdaContractResponse> {
    return this.apiService.request<CreateNdaContractResponse>(
      "/onboarding/create-nda",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      },
    );
  }

  async getEventDetails(eventUri: string): Promise<EventDetailsResponse> {
    return this.apiService.request<EventDetailsResponse>(
      `/onboarding/event-details?eventUri=${encodeURIComponent(eventUri)}`,
      {
        method: "GET",
        requireAuth: false,
      },
    );
  }

  async getNdaStatus(envelopeId: string): Promise<NdaStatusResponse> {
    return this.apiService.request<NdaStatusResponse>(
      `/onboarding/nda-status?envelopeId=${encodeURIComponent(envelopeId)}`,
      {
        method: "GET",
        requireAuth: false,
      },
    );
  }
}
